/**
 * 「正在播放」浮层条（App 独占、仅「学习」Tab）：常驻底部 TabBar 上方，显示当前听书章节。
 * 用途：锁屏/切后台再回学习页时，一眼看到在播哪篇，点击直达该章详情（快速定位）。
 * 仅当处于学习 Tab、有正在播/暂停的章节且当前未停留在该章详情时显示（详情页已有完整 TtsPlayerBar，避免重复）。
 */
import { useTtsStore } from '../state/tts-store';
import { useLearnStore } from '../state/learn-store';
import { isNativeApp } from '@shared/api/screenGuard';

export default function TtsNowPlayingBar({ inLearnTab }: { inLearnTab: boolean }) {
  const status = useTtsStore((s) => s.status);
  const chapterId = useTtsStore((s) => s.chapterId);
  const chapterTitle = useTtsStore((s) => s.chapterTitle);
  const cursor = useTtsStore((s) => s.cursor);
  const total = useTtsStore((s) => s.total);
  const selectedId = useLearnStore((s) => s.selectedId);

  // 非 App / 非学习 Tab / 无在播章节 / 已在该章详情（有完整播放条）→ 不显示
  if (!isNativeApp() || !inLearnTab || chapterId == null || status === 'idle' || selectedId === chapterId) return null;

  const playing = status === 'playing';
  const loading = status === 'loading';

  const goto = () => {
    window.location.hash = `#/learn/chapter/${chapterId}`;
  };
  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    useTtsStore.getState().toggle();
  };

  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-40"
      style={{ bottom: 'calc(env(safe-area-inset-bottom) + 3.5rem)' }}
    >
      <div className="pointer-events-auto mx-auto flex max-w-6xl items-center gap-3 px-3">
        <div
          role="button"
          tabIndex={0}
          onClick={goto}
          className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white/95 py-2 pl-2 pr-3 shadow-lg backdrop-blur"
        >
          {/* 播放/暂停 */}
          <button
            type="button"
            onClick={toggle}
            aria-label={playing ? '暂停' : '播放'}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white active:bg-indigo-700"
          >
            {playing ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="5" width="4" height="14" rx="1" />
                <rect x="14" y="5" width="4" height="14" rx="1" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 5l12 7-12 7z" />
              </svg>
            )}
          </button>
          {/* 标题 + 进度 */}
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm text-slate-800">{chapterTitle || '听书中'}</div>
            <div className="truncate text-xs text-slate-400">
              {loading ? '加载中…' : total > 0 ? `第 ${cursor + 1}/${total} 句 · 点此定位` : '点此定位到本章'}
            </div>
          </div>
          {/* 定位箭头 */}
          <svg
            className="shrink-0 text-slate-400"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 6l6 6-6 6" />
          </svg>
        </div>
      </div>
    </div>
  );
}
